/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-shadow */
import React from 'react';
import { Box, Typography, Container, Stack } from '@mui/material';

const PrivacyPolicy = () => (
  <Box
    component="main"
    sx={{ flexGrow: 1, position: 'relative', zIndex: 10, pt: { xs: 4, md: 6 }, pb: 6 }}
  >
    <Container maxWidth="md" sx={{ px: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box mb={4} textAlign="left">
        <Typography
          variant="overline"
          color="primary.main"
          fontWeight={800}
          letterSpacing={1.5}
          display="block"
          mb={1}
        >
          LEGAL
        </Typography>
        <Typography
          variant="h3"
          component="h1"
          color="text.primary"
          mb={1.5}
          sx={{
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            letterSpacing: '-0.5px',
            fontWeight: 700,
          }}
        >
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last updated: 18-Apr-2026
        </Typography>
      </Box>

      {/* Content Section */}
      <Box>
        {/* Preamble Box */}
        <Box
          mb={4}
          p={3}
          sx={{ bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5 }}>
            MeruDesk is committed to protecting your privacy and to transparency regarding the data
            about you that we collect and how we use it. This Privacy Policy is designed to let you
            know the personal data and/or personal information about you that CyberMeru Technologies
            Pvt. Ltd., doing business as MeruDesk (“MeruDesk,” “we,” “us,” “our”), collect and
            process when you visit our website (Website) or use the services and
            applications/platforms we offer (Services). We refer to our Website and Services
            together in this Privacy Policy as our Offerings.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5 }}>
            This Privacy Policy applies to our collection and use of your personal data or personal
            information (or equivalent terms). When we refer to your “data” or your “information” in
            this Privacy Policy, we are referring to personal data or personal information as
            defined by applicable law. We use those terms interchangeable throughout this Policy to
            refer to data or information that relates to you as an identifiable person, that
            identifies, relates to, or describes you, or that is reasonably capable of being
            associated with or linked to you.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Please read this Privacy Policy carefully to understand our practices and your rights in
            your data. If you do not agree with this Privacy Policy in general or any part of it,
            you should not access or use our Offerings.
          </Typography>
        </Box>

        <Stack spacing={3}>
          {/* Section 1 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              1. SCOPE OF THIS PRIVACY POLICY
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              There are several ways to use our Offerings or we may collect and process your data.
              For example, you might visit our Website or use our Services under your own account
              for your own purposes, or you might use our Services because you have been invited or
              granted access to our Services by a MeruDesk business customer who uses our Services
              (an “Employer”).
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The data we collect and process and how we use that data depends on how you use or the
              Employer uses our Offerings. Some ways we might collect and process your data are when
              you are a:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>Visitor</strong> to our Website;
              </li>
              <li>
                <strong>User</strong> using our Services independently or through an Employer that
                authorized your access to our Services under the Employer’s account;
              </li>
              <li>
                <strong>Candidate</strong> who has been invited to use our Services by an Employer
                using our Services;
              </li>
            </Box>
          </Box>

          {/* Section 2 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              2. HOW WE COLLECT YOUR DATA
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We collect and process your data in the following ways:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>Automatically.</strong> We collect and process certain data automatically
                when you use our Offerings. This may include technical information about your use of
                the Offerings and the pages you visit, actions you take, and features you use when
                interacting with our Offerings.
              </li>
              <li>
                <strong>Provided By You.</strong> We collect and process the data you provide to us
                when you sign up for, access, use, or request support for our Offerings. We may
                collect this data, for example, when you create a MeruDesk account, log in to our
                Services, submit forms or entries, register for services or offers, post to a forum
                or discussion board, share information on your social media content via our
                Offerings, correspond with us, or participate as a Candidate in Candidate-related
                features used by Employers (such as an assessment, test, or interview) (“Candidate
                Features”).
              </li>
              <li>
                <strong>Provided By Employers.</strong> If you are a User or Candidate, we collect
                and process any data that the Employer inviting or authorizing you to use our
                Services provides to us. You may contact the respective Employer for more
                information about the specific data it provides to us.
              </li>
              <li>
                <strong>Received From Third Parties.</strong> We may collect and process information
                we receive about you from third parties we work with to assist us in providing,
                maintaining, improving, or promoting our Services. These third parties include
                business partners and vendors who collect your data from their various sources,
                social media providers through which you share your data with us, advertising
                networks, analytics providers, search information providers, and third-party
                services that may be integrated into our Offerings.
              </li>
            </Box>
          </Box>

          {/* Section 3 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              3. DATA WE COLLECT AND PROCESS
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The types of data we collect and process depend on which of our Offerings you use and
              how you use those Offerings.
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              3.1. Data We Automatically Collect
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              MeruDesk may collect the following data automatically when you use our Offerings:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>IP Address and Internet or Electronic Activity.</strong> To the extent it
                may relate to you, your IP address and technical data in the form of computer
                connection information, browser data (such as browser type and version), and
                activity such as features accessed, pages visited, preferences, and interactions
                with respect to the Website or Services. MeruDesk may also use log files, cookies
                and similar technologies to collect and process this technical information.
              </li>
            </Box>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              3.2. Data You Provide to Us
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              MeruDesk may collect the following data if you provide it to us in connection with
              your use of our Offerings either for your individual purposes or in connection with an
              Employer’s use of our Offerings:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>Identifiers.</strong> Data that may identify you, such as your name, alias,
                username, email address, non-precise location, social media profiles, telephone
                number, account name.
              </li>
              <li>
                <strong>Professional or Employment Information.</strong> Data related to your
                current or past employment, such as job title, position, salary, work experience,
                and employer.
              </li>
              <li>
                <strong>Characteristics.</strong> Data about your gender or your racial or ethnic
                origin or other characteristics if you provide it to us as part of your individual
                account or if an Employer provides it to us or requests that you provide it to us as
                part of your use of a Candidate Feature. We will ask for your consent before we
                collect any data about your racial or ethnic origin for our own purposes.
              </li>
              <li>
                <strong>Education Information.</strong> Data about your current or past educational
                background, such as institutions attended or certifications or degrees awarded.
              </li>
              <li>
                <strong>Interactions and Results.</strong> To the extent it relates to you, when you
                participate in a Candidate Feature or other interactive feature of our Services
                (such as a test, assessment, or interview), we collect and process data you input
                into the Services, your interactions with and actions taken within the Services,
                audio/visual and written content generated in connection with your use of the
                Services, and the results generated by your use of Services (such as your answers,
                score, or ranking) (together, your “Results”).
              </li>
              <li>
                <strong>Other Provided Data.</strong> Other types of data you provide to us in
                connection with your or an Employer’s use of our Offerings.
              </li>
              <li>
                <strong>Video or Facial Image (for Candidates only).</strong> Certain Candidate
                Features requires MeruDesk (when integrated with MeruBot, Microsoft Teams, Google
                Meet, Zoom etc.) to collect and process your video and facial image for the purpose
                of proctoring, facial matching and detection. We only collect and process your video
                and facial image only if you consent to that collection and processing.
              </li>
            </Box>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              3.3. Data Received From Third Parties
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We may collect and process your data we receive from third parties who have collected
              that data so that we can provide certain Services to Employers searching for potential
              Candidates. These third parties have indicated that they may collect your data from
              internal data sharing networks and from outside sources such as open-sourced datasets,
              publicly available data, governmental records, and other proprietary sources they use.
              The data we receive from these third-party providers may include information described
              above as Identifiers, Professional or Employment Information, Characteristics, and
              Education Information and which may include your name, education, qualifications, job
              title, work history, salary and experience, and contact information that our
              third-party providers have collected.
            </Typography>
          </Box>

          {/* Section 4 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              4. HOW WE USE YOUR DATA
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              4.1. Purpose of Processing
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              MeruDesk uses your data for a variety of business purposes and within our legitimate
              interests. We may use your data to do any of the following:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                Provide, develop, maintain, enhance, or support our Offerings or to enter into or
                perform (or take steps to enter into or perform) under a contract between us and the
                Employer or between us and you;
              </li>
              <li>Provide our Services to the applicable Employer;</li>
              <li>
                Allow Employers to use our Services to search for potential Candidates the Employer
                may want to contact;
              </li>
              <li>
                Detect, prevent, mitigate, or repair errors or technical issues or actual or
                suspected fraud or illegal activity related to our Offerings;
              </li>
              <li>
                Ensure the Offerings are used in compliance with applicable terms and agreements;
              </li>
              <li>
                Communicate with you about our Services or about information we believe may be of
                interest to you, such as to respond to your inquiries, fulfill your requests, send
                announcements or transactional messages about our Offerings, or send you news,
                advertising, promotional, or marketing information about our Services;
              </li>
              <li>
                Understand how you use our Offerings so we can enhance, optimize, or personalize
                them for your or the Employer’s use;
              </li>
              <li>
                We may use your anonymized or de-identified gender or racial or ethnic origin
                information for the purposes of ensuring our Services are unbiased and in compliance
                with applicable non-discrimination laws;
              </li>
              <li>
                Comply with our legal obligations or law enforcement requests or to comply with
                applicable laws;
              </li>
              <li>
                If you are a Candidate, we may use your data to “watermark”, stamp, or label your
                Results or participation to identify you as the person who participated in the
                Candidate Feature;
              </li>
              <li>
                Act in our legitimate interest or for a business purpose in accordance with
                applicable law;
              </li>
              <li>For other specific purposes to which you have consented.</li>
            </Box>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              4.2. Processing Duration
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              If you are a Visitor or Individual Member, we process your data only for as long as
              permitted by law or until you cancel your account. As noted below, you may have a
              legal right to request that we delete or stop processing your data sooner. If you are
              a User or Candidate, we will process your data for as long as permitted by our
              agreement with the Employer or as otherwise required by applicable law.
            </Typography>
          </Box>

          {/* Section 5 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              5. HOW WE DISCLOSE YOUR DATA
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We may disclose the categories of data listed in the “Data We Collect and Process”
              Section of this Privacy Policy to the following third parties:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>Employers.</strong> If you are a Candidate or User, we disclose your data
                (including Results) to the Employer applicable to your use of our Services.
              </li>
              <li>
                <strong>Platform Service Providers.</strong> We use third-party providers to perform
                certain functions of our Services platform. We may disclose your data to these
                third-parties in order to provide our Services to Employers or to you. These
                third-parties include our sub-processors.
              </li>
              <li>
                <strong>Other Third Party Providers.</strong> These may include, among others:
                payment processors to process payments you make to MeruDesk; sales, marketing,
                communication, and advertising services to provide you with information and offers
                about our Services; analytics tools to help us understand your use of our Offerings;
                services to authenticate your identity when you access the Services; technical,
                security, and fraud prevention services; and product development services.
              </li>
              <li>
                <strong>Integrations.</strong> If you are a Candidate and the Employer integrates
                our Services with third-party services used by the Employer, MeruDesk may disclose
                your data to those third-party integrated services as determined by the Employer.
              </li>
              <li>
                <strong>Legal or Compliance Purposes.</strong> If we have a legal or compliance
                obligation that requires we share your data to comply with applicable law, we will
                share your data with any third parties as required for us to comply. This may
                include sharing any gender or racial or ethnic origin data you provide us with third
                parties to help ensure our Services are in compliance with applicable laws.
                Likewise, we may disclose your information to third parties as may be reasonably
                necessary to enforce any agreements between us and you or us and the Employer or to
                protect the rights, property, or safety of any person or entity.
              </li>
              <li>
                <strong>Our Affiliates.</strong> We may disclose your data to an affiliated entity
                that owns, is owned by, or has the same owner(s) as MeruDesk, consistent with the
                terms of the Privacy Policy.
              </li>
              <li>
                <strong>Business Transfers.</strong> If we sell or merge our business or sell all or
                substantially all of our assets (or are preparing to do so), we may disclose your
                information to the prospective buyer or merger entity, your data may be one of the
                assets transferred. In these cases, we will take reasonable steps to ensure your
                data remains subject to protections at least as protective as the terms of this
                Privacy Policy.
              </li>
              <li>
                <strong>Legitimate Interests.</strong> We may disclose your data in other cases
                where it is reasonably necessary to achieve our legitimate interest or a business
                purpose in accordance with applicable law.
              </li>
              <li>
                <strong>With Your Consent.</strong> We may request that you separately consent to us
                disclosing your data for a specific purpose and, in those cases, we will only
                disclose your data with your consent.
              </li>
            </Box>
          </Box>

          {/* Section 6 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              6. HOW WE MAY USE AUTOMATED PROCESSING
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              6.1. AI Features Generally
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              If you use our Services as a Candidate, or if we collect your data as a potential
              Candidate, some features of our Services allow Employers to enable the use of
              artificial intelligence, machine learning, and/or other automated processing that
              relates to you (our “AI Features”).
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>Results and Employer Discretion.</strong> The results generated by our AI
                Features are made available to the Employer. The Employer retains ultimate
                discretion as to how to interpret and act on (or not act on) those results.
              </li>
              <li>
                <strong>Not for Unique Identification.</strong> We do not use any data collected or
                processed via our AI Features for the purpose of uniquely identifying you.
              </li>
              <li>
                <strong>No Training on Your Data.</strong> We do not train any of our AI Feature
                models on your personal data, nor do we authorize our third party provides of any AI
                Features to train their models on your data.
              </li>
            </Box>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              6.2. Candidate Detection AI Features
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              If you participate in our AI Features as a Candidate, those AI Features may use
              artificial intelligence or machine learning to monitor a Candidate’s use of the
              Candidate Feature to, depending on the AI Feature, capture a Candidate's video and
              facial image taken from your webcam or device at intervals during your participation
              and analyze those images for the purpose of detecting changes to the facial images, or
              to analyze patterns of your coding and interactions and behavior within a Candidate
              Feature to indicate whether answers may be copied from or generated or assisted by
              using another unauthorized source. We will provide Candidates with notice and ask for
              your consent before you participate in a Candidate Feature that uses AI Features. If
              you do not consent to the use of the AI Feature, you may not be able to, participate
              in that Candidate Feature / use our entire Service.
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              6.3. Potential Candidate AI Features
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Employers may use other AI Features to search for potential Candidates based on data
              we have collected or received. In this case, our AI Features will process your data to
              determine whether you maybe a potential Candidate that meets the Employer's search
              criteria and return those results to the Employer. The Employer may elect to contact
              you as a potential Candidate. When processing your data as a potential Candidate, we
              only process your data prior to the Employer's decision whether to engage with you and
              prior to your initial interaction with the Employer, if any, and prior to the Employer
              making any employment-related decisions related to you.
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              6.4. Other Automated Processing
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Aside from our AI Features, we and our service providers may use other automated
              processing and/or automated decision making where it is necessary to enter into or
              perform a contract between you and MeruDesk or to detect or prevent fraud, abuse, or
              violations of our Offerings. By using our Services, you consent to our use of
              automated processing/decision making for this purpose. We may update the automated
              techniques and tools we use over time.
            </Typography>
          </Box>

          {/* Section 7 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              7. COOKIES AND SIMILAR TECHNOLOGY
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We use cookies or similar technologies when you visit our Website. These are
              commonly-used data collection tools that include small text files placed on your
              computer or device by websites you visit or HTML-formatted emails you open to help our
              Website work more efficiently and provide you information about our Services.
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>How We Use Cookies.</strong> We may use cookies, web beacons, pixel tags,
                anonymous tracking, or similar technologies on our Website as follows:
                <ul style={{ marginTop: '8px' }}>
                  <li>Essential Cookies to authenticate your identity and prevent fraud;</li>
                  <li>
                    Analytics Cookies to track information about how our Offerings are used so we
                    can make improvements, test new pages, features or functionality, or tailor
                    information presented to you based on your usage of the Offerings; and
                  </li>
                  <li>
                    Advertising Cookies to provide Visitors or Individual Members with information
                    and advertising about our Services.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Your Cookies Choices.</strong> You have the choice to opt-out of the use of
                certain cookies. You may opt-out of our use of cookies by activating the appropriate
                setting on your browser to block cookies. Also, when we use cookies for advertising
                purposes, most advertising networks offer a way to opt out of targeted advertising.
                For more information about your cookies choices, please visit: Your Ad Choices or
                Your Online Choices. You may opt out by clicking here: Your Ad Choices Opt-Out or
                Network Advertising Opt-Out (or if located in the European Union, by clicking here:
                Online Choices Opt-Out). Please note that this does not opt you out of being served
                all advertising - you will continue to receive generic advertisements. Also, if you
                block cookies, you may not be able to access certain parts of our Website or receive
                certain Services.
              </li>
              <li>
                <strong>Do-Not-Track.</strong> There are several ways you can prevent tracking of
                your online activity. One of them is by setting a preference in your browser to
                alert websites you visit that you do not want them to collect certain information
                about you. This is referred to as a “Do-Not-Track” signal. Please note that our
                Website may not recognize or take action in response to Do-Not-Track signals from
                your browser.
              </li>
            </Box>
          </Box>

          {/* Section 8 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              8. YOUR RIGHTS IN YOUR DATA
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              8.1. GDPR (European Economic Area)
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              If you are located in the European Economic Area, you have rights in your data under
              the General Data Protection Regulation (“GDPR”).
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              When using our Services as a User or Candidate, then the Employer inviting you or
              granting you access to our Services is the “data controller” and MeruDesk is the “data
              processor” under the GDPR. In that case, MeruDesk will process your data in accordance
              with the agreement between MeruDesk and the Employer.
            </Typography>
            <Typography variant="subtitle2" color="text.primary" mt={2} mb={1}>
              GDPR Rights
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              If we collect or process your data other than as a User or Candidate, then MeruDesk is
              the “data controller” under the GDPR. In that case, you have the following rights in
              your data:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                The right of access to obtain confirmation as to whether or not personal data are
                being processed and to obtain certain information about your data we process. You
                can find much of that information within this Privacy Policy;
              </li>
              <li>
                The right to request that we rectify any data you believe is inaccurate and that we
                complete any data you believe is incomplete;
              </li>
              <li>
                The right to request that we erase your data in certain situations, including where
                it is no longer necessary for us to hold the data, where you withdraw your previous
                consent to our processing of the data and we have no other legal basis to continue
                processing the data, where you object to our legitimate interest for processing
                without us having overriding grounds, or where you believe our processing is
                unlawful;
              </li>
              <li>
                The right to request that we restrict processing of your data in certain situations;
              </li>
              <li>
                The right to portability of your data, which allows you to request that we transfer
                your data to another organization or directly to you;
              </li>
              <li>
                The right to object to/stop our processing of your data where processing is based on
                our legitimate interests and we do not have an overriding legitimate grounds to
                continue processing. You have the right to object to our processing of your data for
                marketing purposes, including where we build profiles for such purposes, and we will
                stop processing your data for that purpose;
              </li>
              <li>
                The right to not be subject to a decision based solely on automated processing
                without your consent, including building profiles for such purposes, where the
                decision would have a legal effect on you or produce a similarly significant effect,
                except to the extent automated processing is permitted by the GDPR. Note that, if an
                AI Feature requires your consent and you do not consent, you will not be able to
                participate in Candidate Features that use the AI Feature.
              </li>
              <li>
                In addition, if you consider our processing of your data to be in violation of your
                rights under GDPR, you have the right to complain to your applicable data protection
                supervisory authority.
              </li>
            </Box>
            <Typography variant="subtitle2" color="text.primary" mt={2} mb={1}>
              Limits on GDPR Rights
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Note that your rights in your data listed above have some limits under GDPR. For
              example, you may not be able to exercise your GDPR rights if fulfilling your request
              would adversely affect other individuals or our trade secrets or intellectual
              property, where there is an overriding public interest, or where we are required by
              law to retain your personal data.
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              8.2. Residents of California and Other Applicable U.S. States
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              If you are a resident of California, your rights in your data are governed by the
              California Consumer Privacy Act, as amended by the California Privacy Rights Act (the
              “CCPA”). If you are a resident of another U.S. state having laws governing your data
              privacy rights in your personal data or personal information, your rights may be
              governed by the applicable data protection laws of your jurisdiction.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              When using our Services as a User or Candidate, the Employer granting you access to
              our Services is the “business” under the CCPA, and MeruDesk is the “service provider”
              under the CCPA. In that case, MeruDesk will process your data in accordance with the
              agreement between MeruDesk and the Employer.
            </Typography>
            <Typography variant="subtitle2" color="text.primary" mt={2} mb={1}>
              CCPA Rights
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              When we collect your data other than as a User or Candidate, then MeruDesk is the
              “business” under CCPA. In that case, you have the following rights:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                The right to request that we delete the personal information we have collected from
                you (and direct our third-party service providers to do the same). This right is
                subject to several exceptions where the information is necessary for us or a third
                party to do any of the following:
                <ul style={{ marginTop: '8px' }}>
                  <li>
                    complete the transaction for which the personal information was collected,
                    provide services to you, or otherwise perform a contract between you and
                    MeruDesk;
                  </li>
                  <li>help to ensure security and integrity of the Offerings;</li>
                  <li>debug to identify and repair errors that impair our Offerings;</li>
                  <li>
                    exercise free speech, ensure the right of another to exercise that their right
                    of free speech, or exercise another right provided for by law;
                  </li>
                  <li>
                    comply with the California Electronic Communications Privacy Act pursuant to
                    Chapter 3.6 of Title 12 of Part 2 of the California Penal Code;
                  </li>
                  <li>
                    engage in public or peer-reviewed scientific, historical, or statistical
                    research in the public interests that conforms or adheres to all other
                    applicable ethics and privacy laws when our deletion of the information is
                    likely to render impossible or seriously impair the ability to complete such
                    research, if you have provided your informed consent;
                  </li>
                  <li>
                    making internal uses that are reasonably aligned with your expectations based on
                    your relationship with us and compatible with the context in which you provided
                    the information; or
                  </li>
                  <li>comply with a legal obligation.</li>
                </ul>
              </li>
              <li>
                The right to request that we correct any inaccurate personal information we maintain
                about you, taking into account the nature of the personal information and the
                purposes of processing the personal information.
              </li>
              <li>
                The right to know/request that we disclose to you the following details about your
                data we collect: the categories of personal information we have collected; the
                categories of sources from which we collect your data; the business or commercial
                purpose for collecting, selling, or sharing your data; the categories of third
                parties to whom we disclose your data; and the specific pieces of data we have
                collected about you. We have sought to include this information in this Privacy
                Policy.
              </li>
              <li>
                The right to not be discriminated against for exercising any of your rights under
                the CCPA.
              </li>
              <li>
                The right to limit our use of your “Sensitive Personal Information” (as defined by
                the CCPA), to that which is necessary to provide reasonably expected services to
                you, if we collect any “Sensitive Personal Information” for our own purposes outside
                of providing our Services to an Employer.
              </li>
            </Box>
            <Typography variant="subtitle2" color="text.primary" mt={2} mb={1}>
              Data Collected Over the Past 12 Months
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              The categories of personal information we may have collected over the past 12 months
              are listed in the “Data We Collect and Process” section of this Privacy Policy.
            </Typography>
            <Typography variant="subtitle2" color="text.primary" mt={2} mb={1}>
              We Do Not Sell or Share Personal Information
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              MeruDesk does not sell your information to third parties, nor do we share your
              information with third parties for any cross-context behavioral advertising purposes
              as defined by the CCPA. If, in the future, we intend to sell or share your information
              as defined by the CCPA, we will provide you notice and you will have the right to
              opt-out of the sale of your personal information via a “Do Not Sell My Personal
              Information” or similar link and to request certain information about our disclosure
              of personal information to third parties.
            </Typography>

            <Typography variant="subtitle1" color="text.primary" mt={2.5} mb={1}>
              8.3. Exercising Rights in Your Data
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                color: 'text.secondary',
                typography: 'body1',
                '& li': { mb: 1 },
              }}
            >
              <li>
                <strong>Other than as a User or Candidate.</strong> To exercise your rights other
                than as a User or Candidate, please submit a support request to either of the
                following:
                <ul style={{ marginTop: '8px' }}>
                  <li>Via email: privacy@MeruDesk.com</li>
                </ul>
                <Box mt={2}>
                  We intend to respond to a request for access or deletion within 1 month of
                  receiving a request under GDPR, within 45 days of receiving a request under the
                  CCPA, or within the time period permitted under any other applicable law. If we
                  need more time, we will let you know the reason and the extension period. We may
                  require that you verify your identity in order for us to process your request. If
                  we are unable to verify your identity, we may deny your request and we will let
                  you know if that is the case.
                  <br />
                  <br />
                  Note that deleting or restricting the processing of your data may impact any
                  features of our Services that require that data to function. As a result, if you
                  delete or restrict processing of your personal information, you may no longer be
                  able to use all or part of the Services.
                </Box>
              </li>
              <li style={{ marginTop: '16px' }}>
                <strong>As a User or Candidate.</strong> To exercise your rights in your data as a
                User or Candidate, please contact your respective Employer. We will follow the
                Employer’s instructions regarding your data.
              </li>
            </Box>
          </Box>

          {/* Section 9 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              9. CHILDREN
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We do not knowingly collect personal data from children under age 18 for any purpose.
              If we learn that we have collected personal information of a child under age 18, we
              will attempt to delete that personal data as soon as possible and take any other
              measures required under applicable law.
            </Typography>
          </Box>

          {/* Section 10 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              10. CHANGES TO PRIVACY POLICY
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              We may make changes to this Privacy Policy at any time. The “Last Updated” date at the
              top of this page refers to the last date this Privacy Policy was updated. Changes to
              this Privacy Policy are effective immediately upon notice, which notice we may give by
              any reasonable means permitted by law, including, but not limited to, by posting a
              revised version of this Privacy Policy or other notice on the Website. Please check
              back frequently for any updates or changes. We regularly review our policies and
              practices to ensure compliance with all applicable legal, statutory, regulatory, and
              contractual requirements, including the Digital Personal Data Protection Act, 2023
              (India), EU GDPR (where applicable), and global standards related to information
              security and data protection. This includes periodic legal assessments and updates
              aligned with ISO/IEC 27001:2022 requirements.
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Container>
  </Box>
);

export default PrivacyPolicy;
